import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  Shuffle,
  Search,
  Sparkles,
  X,
  Info,
  Code,
  CheckCircle2,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import logo from "../../assets/icons//mocionLogo.svg";
import {
  useConfirmKnockoutTeamsMutation,
  useGetKnockoutBracketQuery,
} from "../../lib/redux/services/sections/Leagues-Tournaments";
import { useNavigate, useParams } from "react-router-dom";
import Button from "../../components/global/Button";
import { cn, toastMessage } from "../../utils/utils";
import { toast } from "react-toastify";
import ModalButton from "../../components/global/ModalButton";
import ActionModal from "../../components/global/ActionModal";

const MatchCard = ({ matchData, matchNumber, searchQuery, stageName }: any) => {
  const [team1, team2] = matchData || [];

  const isHighlighted = useCallback(
    (team: any) => {
      if (!searchQuery.trim() || !team) return false;
      const q = searchQuery.toLowerCase();
      return (
        (team.name && team.name.toLowerCase().includes(q)) ||
        (team.players &&
          team.players.some(
            (p: any) => p.name && p.name.toLowerCase().includes(q),
          ))
      );
    },
    [searchQuery],
  );

  const renderTeamRow = (teamBadgeNumber: number, team: any) => {
    if (!team) return null;
    const highlighted = isHighlighted(team);

    return (
      <div
        className={`relative flex items-center justify-between p-3 transition-all duration-200 select-none rounded-lg mx-1 ${
          highlighted ? "ring-2 ring-indigo-500 bg-indigo-50/40" : ""
        }`}
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {/* Seed / Position Badge Number */}
          <div className="w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs shrink-0 transition-colors bg-slate-100 text-slate-700">
            {teamBadgeNumber}
          </div>

          {/* Team and Player Details */}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-sm font-bold truncate text-slate-900">
                {team.name}
              </span>
            </div>

            {/* Players List with Level Badges */}
            <div className="flex items-center gap-1.5 text-xs text-slate-500 flex-wrap mt-0.5 font-normal">
              {team.players &&
                team.players.map((player: any, idx: number) => (
                  <React.Fragment key={player.id || idx}>
                    <span className="truncate max-w-[130px] font-medium text-slate-600">
                      {player.name}
                    </span>
                    <span className="bg-indigo-50/90 text-indigo-600 font-semibold px-2 py-0.5 rounded-full text-[11px] border border-indigo-100/80 tracking-tight shrink-0">
                      {typeof player.level === "number"
                        ? player.level.toFixed(2)
                        : player.level}
                    </span>
                    {idx < team.players.length - 1 && (
                      <span className="text-slate-300 font-bold text-[10px] select-none">
                        .
                      </span>
                    )}
                  </React.Fragment>
                ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white border border-slate-200/90 rounded-2xl overflow-hidden transition-all duration-200 shadow-xs hover:shadow-md">
      {/* Match Header */}
      <div className="px-4 pt-3.5 pb-1 flex items-center justify-between">
        <span className="text-xs font-bold text-slate-900 tracking-tight">
          Match {matchNumber}
        </span>
        {stageName && (
          <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">
            {stageName}
          </span>
        )}
      </div>

      {/* Team 1 */}
      {renderTeamRow(1, team1)}

      {/* VS Separator Divider Strip */}
      <div className="bg-slate-50/80 border-y border-slate-100/90 py-0.5 flex justify-center items-center my-1 relative">
        <span className="text-[11px] font-black text-orange-500 uppercase tracking-widest px-2 bg-slate-50">
          vs
        </span>
      </div>

      {/* Team 2 */}
      {renderTeamRow(2, team2)}
    </div>
  );
};

type props = {
  type: "tournament" | "league";
};
export default function KnockoutBracket({ type }: props) {
  const { id } = useParams();
  const [apiPayload, setApiPayload] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [matchesList, setMatchesList] = useState([]);
  const [isShuffling, setIsShuffling] = useState(false);
  const [confirmKnockoutTeams] = useConfirmKnockoutTeamsMutation();
  const { data, isSuccess } = useGetKnockoutBracketQuery({
    event_id: id,
    booking_type: type,
  });
  const navigate = useNavigate();
  // Display notification message

  useEffect(() => {
    if (data && isSuccess) {
      setMatchesList(data.data);
    }
  }, [data]);

  const totalTeams = useMemo(() => {
    return (
      matchesList?.reduce(
        //@ts-ignore
        (acc, match) => acc + (match ? match.length : 0),
        0,
      ) || 0
    );
  }, [matchesList]);

  const totalPlayers = useMemo(() => {
    return (
      matchesList?.reduce((acc, match) => {
        //@ts-ignore
        return (
          acc +
          //@ts-ignore
          match.reduce((tAcc, team) => tAcc + (team?.players?.length || 0), 0)
        );
      }, 0) || 0
    );
  }, [matchesList]);
  const handleConfirmKnockoutTeams = async () => {
    const orderedTeamIds = matchesList?.flat().map((t: any) => t.team_id);

    try {
      await toast.promise(
        confirmKnockoutTeams({
          data: {
            ordered_team_ids: orderedTeamIds,
            event_id: id,
          },
          booking_type: type,
        }).unwrap(),
        toastMessage(),
      );
      navigate(`/competitive/${type}s/edit/schedule/knockouts/${id}`);
    } catch (error) {}
  };

  // Split matches for balanced 2-column layout
  const leftMatches = useMemo(() => {
    const mid = Math.ceil(matchesList.length / 2);
    return matchesList.slice(0, mid);
  }, [matchesList]);

  const rightMatches = useMemo(() => {
    const mid = Math.ceil(matchesList.length / 2);
    return matchesList.slice(mid);
  }, [matchesList]);
  const handleShuffleTeams = () => {
    setIsShuffling(true);

    setTimeout(() => {
      // Collect all teams and shuffle
      const allTeams = matchesList.flat();
      const shuffledTeams = [...allTeams].sort(() => Math.random() - 0.5);

      // Reconstruct match pairs
      const newMatches = [];
      for (let i = 0; i < shuffledTeams.length; i += 2) {
        if (shuffledTeams[i] && shuffledTeams[i + 1]) {
          newMatches.push([shuffledTeams[i], shuffledTeams[i + 1]]);
        } else if (shuffledTeams[i]) {
          newMatches.push([shuffledTeams[i]]);
        }
      }

      //@ts-ignore
      setMatchesList(newMatches);
      // setJsonInput(JSON.stringify(updatedPayload, null, 2));
      setIsShuffling(false);
      toast.success("Teams successfully reshuffled!");
    }, 300);
  };
  const handleResetToDefault = () => {
    setMatchesList(data.data);
    toast.success("Reset to default order");
  };
  return (
    <div className="max-h-[calc(100dvh-220px)] min-h-[calc(100dvh-240px)] overflow-auto customScroll    p-4 md:p-8 flex flex-col items-center">
      {/* Toast Notification */}

      {/* Main Container */}
      <div className="w-full   text-left py-5">
        <h1 className="text-2xl font-bold text-primary tracking-tight ">
          Knockout Bracket
        </h1>
        <p className="text-sm font-medium text-slate-500 mt-0.5">
          {/* @ts-ignore */}
          {data?.stage_name || "Quarter finals"} . {totalTeams} teams
        </p>
      </div>
      <div className="w-full  space-y-6 rounded-2xl bg-white">
        {/* Top Header Section */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          {/* Quick Controls */}
        </div>

        {/* Main Stage Container */}
        <div className="bg-white rounded-2xl border border-slate-100/80 p-5 md:p-7 shadow-xs">
          {/* Card Sub-Header */}
          <div className="flex items-center justify-between mb-6 pb-2">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-extrabold text-primary">
                {/* @ts-ignore */}
                {data?.stage_name || "Quarter finals"}
              </h2>
            </div>

            {/* Shuffle Teams Action Button */}
            <ModalButton
              modalContent={
                <ActionModal
                  title="Warning"
                  subtitle="Are you sure you want to shuffle? Once you press Ok, the group order will change!"
                  secondButtonCallback={(toggleModal: any) => {
                    handleShuffleTeams();
                    toggleModal();
                  }}
                  buttonsContainerClassName="flex flex-row"
                  firstButtonTitle="No"
                  secondButtonTitle="Yes"
                  useApiAction={() => {}}
                />
              }
              buttonContent={
                <>
                  {" "}
                  <Shuffle className={`w-4 h-4 text-primary `} />
                  <span>Shuffle teams</span>
                </>
              }
              buttonStyle="bg-[#d2ed38] hover:bg-[#c3e02d] text-primary font-bold text-sm px-4 py-2.5 rounded-2xl shadow-xs transition-all flex items-center gap-2 select-none hover:shadow-sm active:scale-95 "
            />
            {/* <button
              onClick={handleShuffleTeams}
              className={`bg-[#d2ed38] hover:bg-[#c3e02d] text-primary font-bold text-sm px-4 py-2.5 rounded-2xl shadow-xs transition-all flex items-center gap-2 select-none hover:shadow-sm active:scale-95 `}
            ></button> */}
          </div>

          {/* Dynamic Grid Layout (2 Columns on Desktop) */}
          <div
            className={`grid grid-cols-1 lg:grid-cols-2 gap-5 transition-opacity duration-200 `}
          >
            {/* Left Column Matches */}
            <div className="space-y-5">
              {leftMatches.map((matchData, idx) => (
                <MatchCard
                  key={idx}
                  matchNumber={idx + 1}
                  matchData={matchData}
                  searchQuery={searchQuery}
                  //@ts-ignore
                  stageName={data?.stage_name}
                />
              ))}
            </div>

            {/* Right Column Matches */}
            <div className="space-y-5">
              {rightMatches.map((matchData, idx) => (
                <MatchCard
                  key={leftMatches.length + idx}
                  matchNumber={leftMatches.length + idx + 1}
                  matchData={matchData}
                  searchQuery={searchQuery}
                  //@ts-ignore
                  stageName={data?.stage_name}
                />
              ))}
            </div>
          </div>

          {/* Bottom Summary Bar */}
          <div className="mt-8 pt-4 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between text-xs text-slate-400 gap-2">
            <div className="flex items-center gap-4 text-slate-500 font-medium">
              <span>
                Total Matches:{" "}
                <strong className="text-slate-800">{matchesList.length}</strong>
              </span>
              <span>
                Total Teams:{" "}
                <strong className="text-slate-800">{totalTeams}</strong>
              </span>
              <span>
                Total Players:{" "}
                <strong className="text-slate-800">{totalPlayers}</strong>
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full bg-white p-5 flex justify-between items-center ">
        <Button
          customeStyle=" flex  items-center justify-center border border-primary rounded-full w-full text-[16px] font-bold py-5 gap-4"
          text={""}
          width="min-w-[150px]"
          onClick={() => {
            navigate(`/competitive/tournaments/rounds/${id}?page=groups`);
          }}
        >
          <ChevronLeft />
          Back
        </Button>
        <div className="flex gap-7 items-center">
          <Button
            customeStyle={cn(
              " flex  items-center justify-center bg-primary text-primaryYellow rounded-full w-full text-[16px] font-bold py-5 gap-4",
            )}
            text={""}
            width="min-w-[150px]"
            onClick={async () => {
              await handleConfirmKnockoutTeams();
            }}
          >
            Next
            <ChevronRight />
          </Button>
        </div>
      </div>
    </div>
  );
}
